import { QuoteRatingSearchRequestQuery, QuoteRatingSearchResponse } from '@lio/api-types';
import { config } from '@services/config';
import { quoteRatingQueryKey } from '@services/quoteRating/keys';
import { getSearchQuoteRatingsUrl } from '@services/quoteRating/quoteRating.helpers';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';

/** infinite query */
export const useSearchQuoteRatings = ({
  quoteId,
  enabled,
  params = {},
}: {
  quoteId?: string;
  enabled?: boolean;
  params: QuoteRatingSearchRequestQuery;
}) => {
  const client = useClient();
  const query = getSearchQuoteRatingsUrl({ ...params });
  const url = `quote/${quoteId}/rating?${query.toString()}`;
  const result = useInfiniteQuery<QuoteRatingSearchResponse, Error>({
    queryKey: quoteRatingQueryKey.searchWithParams(quoteId, params),
    queryFn: ({ pageParam = 0 }) => client(`${url}&pageOffset=${pageParam}`).json<QuoteRatingSearchResponse>(),
    enabled: Boolean(quoteId && enabled),
    staleTime: 15000,
    initialPageParam: 0,
    placeholderData: (previousData) => previousData,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.pagination) return undefined;
      if (!lastPage.pagination.pageLimit) return undefined;
      if (!lastPage.pagination.total) return undefined;
      const { total, pageLimit } = lastPage.pagination;
      const fetchedPageCount = allPages.length;
      const nextPageOffsetValue = fetchedPageCount * pageLimit;
      if (nextPageOffsetValue > total) return undefined;
      return nextPageOffsetValue;
    },
  });
  return result;
};

export const useSearchQuoteRatingsNonInfinite = (
  quoteId: string,
  enabled: boolean,
  params?: QuoteRatingSearchRequestQuery,
) => {
  const client = useClient();
  const query = getSearchQuoteRatingsUrl({ ...params });

  const result = useQuery<QuoteRatingSearchResponse, Error>({
    queryKey: quoteRatingQueryKey.searchWithoutInfiniteParams(quoteId, params),
    queryFn: async () => client.get(`quote/${quoteId}/rating?${query}`).json<QuoteRatingSearchResponse>(),
    enabled: Boolean(quoteId && enabled),
    ...config,
  });

  return { ...result, ratingList: result.data?.items || [] };
};
